
















































import { computed, defineComponent, getCurrentInstance, ref, toRef, watch } from "@vue/composition-api";
import { Group } from "@scrile/api-provider/dist/api/GroupsProvider";
import { InputMassMessage, MassMessage } from "@scrile/api-provider/dist/api/MassMessageProvider";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import BaseButton from "@/components/BaseButton";
import BaseIcon from "@/components/BaseIcon";
import BasePage from "@/components/BasePage";
import GroupListSelection from "@/components/GroupListSelection";
import PageTitle from "@/components/PageTitle";
import MassMessageConfirm from "./components/MassMessageConfirm.vue";
import MassMessageEditView from "./components/MassMessageEditView.vue";

export default defineComponent({
  name: "MassMessageEdit",
  props: {
    groupList: { type: Array as () => Group[], required: true },
    massMessage: { type: Object as () => MassMessage },
    groupIds: { type: Array as () => string[] },
    defaultBack: { type: Object },
    selectedGroupIds: { type: Array as () => string[] },
  },
  components: {
    BaseIcon,
    BasePage,
    PageTitle,
    BaseButton,
    MassMessageConfirm,
    MassMessageEditView,
    GroupListSelection,
  },
  setup(props, { emit }) {
    const isShowCancelConfirm = ref<boolean>(false);
    const groupIdsLocal = ref<string[]>(props.selectedGroupIds ?? []);
    const excludedGroupIdsLocal = ref<string[]>([]);
    const recipientCount = ref<number>(0);
    const isGroupMode = ref<boolean>(false);

    const isEditMode = computed<boolean>(() => !!props.massMessage?.id);

    const resetGroupsSelection = ref<() => void>();

    const vOnTitle = computed(() => {
      if (!isGroupMode.value && !props.massMessage) {
        return undefined;
      }
      if (isGroupMode.value && groupIdsLocal.value.length === 0 && !props.massMessage) {
        return undefined;
      }

      return {
        click: () => {
          if (!isGroupMode.value && props.massMessage) {
            emit("back");
          } else {
            isGroupMode.value = false;
            resetGroupsSelection.value && resetGroupsSelection.value();
          }
        },
      };
    });

    watch(
      recipientCount,
      () => {
        if (
          recipientCount.value === 0 &&
          !props.massMessage &&
          (!props.selectedGroupIds || props.selectedGroupIds.length === 0)
        ) {
          isGroupMode.value = true;
        }
      },
      { immediate: true }
    );

    watch(
      toRef(props, "massMessage"),
      () => {
        if (!props.massMessage) return;
        groupIdsLocal.value.push(...props.massMessage.groupIds);
        excludedGroupIdsLocal.value.push(...props.massMessage.excludedGroupIds);
      },
      { immediate: true }
    );

    const loading = ref<boolean>(false);
    const vm = getCurrentInstance();
    const title = computed(() => {
      if (isGroupMode.value && recipientCount.value > 0) return vm?.$tc($t("recipient"), recipientCount.value);
      if (isGroupMode.value && recipientCount.value < 1) return vm?.$t("Select recipients");
      if (isEditMode.value) return $t("Edit");
      return $t("Create a mass message");
    });
    const onSubmit = async (massMessage: InputMassMessage) => {
      try {
        loading.value = true;
        const updatedMassMessage = await providers.MassMessageProvider.update({ data: massMessage });
        emit("save", updatedMassMessage);
      } finally {
        loading.value = false;
      }
    };
    const onConfirm = async () => {
      if (!props.massMessage) return;
      try {
        loading.value = true;
        const result = await providers.MassMessageProvider.cancel({ id: props.massMessage.id });
        result && emit("delete");
      } finally {
        loading.value = false;
      }
    };
    const onContinue = async ({
      groupIds,
      excludedGroupIds,
      recipientCount: recipientCountNew,
    }: {
      groupIds: string[];
      excludedGroupIds: string[];
      recipientCount: number;
    }) => {
      recipientCount.value = recipientCountNew;
      groupIdsLocal.value = groupIds;
      excludedGroupIdsLocal.value = excludedGroupIds;
      isGroupMode.value = false;
    };

    return {
      title,
      loading,
      vOnTitle,
      isEditMode,
      isGroupMode,
      groupIdsLocal,
      recipientCount,
      isShowCancelConfirm,
      resetGroupsSelection,
      excludedGroupIdsLocal,
      onSubmit,
      onConfirm,
      onContinue,
    };
  },
});
